// Copyright (c) 2026 aiApas LLC. All Rights Reserved.
// GuardRok — Proprietary and Confidential. Patent Pending.
// Unauthorized use or distribution is prohibited.

import { useState, useRef, useEffect } from "react"
import { ChevronDown } from "lucide-react"
import { LANGUAGES, LangCode } from "../i18n/translations"
import { useLang } from "../i18n/LanguageContext"

export default function LanguageSwitcher() {
  const { lang, setLang } = useLang()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const current = LANGUAGES.find(l => l.code === lang) ?? LANGUAGES[0]

  // Close when clicking outside
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  function select(code: LangCode) {
    setLang(code)
    setOpen(false)
  }

  return (
    <div ref={ref} style={{ position: "relative" }}>
      {/* Trigger button */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: "flex", alignItems: "center", gap: 5,
          background: open ? "#0f172a" : "transparent",
          border: "1px solid", borderColor: open ? "#334155" : "transparent",
          borderRadius: 7, padding: "5px 9px",
          color: "#9ca3af", fontSize: 13, cursor: "pointer",
          transition: "all 0.15s",
          lineHeight: 1,
        }}
        aria-label="Switch language"
        title="Switch language"
      >
        <span style={{ fontSize: 15 }}>{current.flag}</span>
        <span style={{ fontWeight: 500 }}>{current.code.toUpperCase()}</span>
        <ChevronDown size={12} style={{ opacity: 0.6, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", right: 0,
          background: "#0a1122", border: "1px solid #1e293b",
          borderRadius: 10, padding: "6px 0",
          minWidth: 168, zIndex: 9999,
          boxShadow: "0 12px 32px rgba(0,0,0,0.5)",
          maxHeight: 320, overflowY: "auto",
        }}>
          {LANGUAGES.map(l => (
            <button
              key={l.code}
              onClick={() => select(l.code)}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                width: "100%", background: "none", border: "none",
                padding: "7px 14px", cursor: "pointer", textAlign: "left",
                color: l.code === lang ? "#fff" : "#94a3b8",
                backgroundColor: l.code === lang ? "#1e293b" : "transparent",
                fontSize: 13, fontFamily: "system-ui",
                transition: "background 0.1s",
              }}
              onMouseEnter={e => { if (l.code !== lang) (e.currentTarget as HTMLButtonElement).style.backgroundColor = "#0f172a" }}
              onMouseLeave={e => { if (l.code !== lang) (e.currentTarget as HTMLButtonElement).style.backgroundColor = "transparent" }}
            >
              <span style={{ fontSize: 16, flexShrink: 0 }}>{l.flag}</span>
              <span style={{ flex: 1 }}>{l.label}</span>
              {l.code === lang && (
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#E63946", flexShrink: 0 }} />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
